import React, { useCallback } from "react";
import {
  CustomEventTarget,
  createCustomEvent,
  isDefaultPrevented,
  useFireCustomEvent,
} from "@adsk/acc-issues-core/providers/CustomEventContext";
import { Issue, CustomEventType } from "@adsk/acc-issues-types";
import { getPlacementOriginContext } from "@adsk/acc-issues-core/utils/data/placement";
import {
  PlacementDeepLinkType,
  useGetDeepLinkInfo,
} from "@adsk/acc-issues-core/utils/hooks/useDefaultPlacementLink";
import {
  trackPlacementLinkClick,
  trackPlacementDeepLinkClick,
  trackPlacementFallbackClick,
} from "../../utils/analytics/placementFieldTracking";
import A from "@adsk/alloy-react-a";
import { useIssuesRemoteOverrideUrl } from "../../hooks/useIssuesRemoteOverrideUrl";
import styled from "styled-components";
import Theme from "@adsk/alloy-react-theme";

const PlacementLink = styled(A)<{ enableBreakWord?: boolean }>`
  ${props => (props.enableBreakWord ? "word-break: break-word;" : "")}
  /* As TableCell has color: inherit prop, we need to override the color for the link */
  && {
    color: ${Theme.colors.adskBlue700};

    &:hover {
      color: ${Theme.colors.adskBlue500};
    }
  }
`;

const PlacementText = styled.span<{ enableBreakWord?: boolean }>`
  ${props => (props.enableBreakWord ? "word-break: break-word;" : "")}
`;

export const PlacementDisplay = ({
  placementLink,
  placementText,
  shouldShowLink,
  issue,
  enableBreakWord = false,
}: {
  placementLink: string;
  placementText: string;
  shouldShowLink: boolean;
  issue: Issue;
  enableBreakWord?: boolean;
}) => {
  const fireCustomEvent = useFireCustomEvent();
  const getDeepLinkInfo = useGetDeepLinkInfo();
  const originContext = getPlacementOriginContext(issue);
  const { linkType, product, tool } = getDeepLinkInfo(originContext);
  const getRemoteUrl = useIssuesRemoteOverrideUrl();

  // Process the URL once at the component level
  const updatedPlacementLink = getRemoteUrl(placementLink);

  const onPlacementClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (fireCustomEvent) {
        const customEventData = createCustomEvent(
          {
            type: CustomEventType.PlacementLinkClick,
            target: CustomEventTarget.Link,
            data: {
              url: updatedPlacementLink, // Use the updated URL for consistency
              issue,
            },
          },
          e
        );
        fireCustomEvent(customEventData);
        if (isDefaultPrevented(customEventData)) {
          e.preventDefault();
        }
      }

      const issueId = issue.id;
      trackPlacementLinkClick({ issueId });
      if (linkType === PlacementDeepLinkType.DEEP_LINK) {
        trackPlacementDeepLinkClick({ issueId, product, tool });
      }
      if (linkType === PlacementDeepLinkType.FALLBACK_DEEP_LINK) {
        trackPlacementFallbackClick({ issueId, product, tool });
      }
    },
    [updatedPlacementLink, fireCustomEvent, linkType, product, tool, issue]
  );
  if (shouldShowLink) {
    return (
      <PlacementLink
        href={updatedPlacementLink}
        target="_blank"
        onClick={onPlacementClick}
        enableBreakWord={enableBreakWord}
      >
        {placementText}
      </PlacementLink>
    );
  }
  return (
    <PlacementText enableBreakWord={enableBreakWord}>
      {placementText}
    </PlacementText>
  );
};
